import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Btn, Card, Field, Input, Select, Textarea, ErrorSummary, SourceTag, StatusChip, Tag, Notice, useFieldErrors } from "../components/kit";
import BodyMap, { RegionList, SIDES } from "../components/BodyMap";
import { SourceSelect } from "../components/Baseline";
import FileUploader, { FileThumb } from "../components/FileUploader";
import { DraftBanner } from "../components/common";
import { api, parseError } from "../lib/api";
import { useDraft, useOnline } from "../lib/draft";
import { fmtDate, todayISO } from "../lib/format";

const BLANK = { region: null, side: "left", sign: "", description: "", text: "", source: null, event_date: "", date_precision: "day", comparison_conditions: "",
  procedure: "", tool: "", area: "", observed_response: "", conditions: "", assistance_required: "", next_step: "", category: null, file_ids: [] };
const TYPE = { observations: "observation", feasibility: "feasibility", restrictions: "restriction", hypotheses: "hypothesis" };

export function useRefList(key) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/reference-lists").then((r) => setItems((r.data.find((x) => x.key === key)?.items || []).filter((i) => i.active !== false))); }, [key]);
  return items;
}

function DateInput({ f, setF }) {
  const { t } = useTranslation();
  const type = f.date_precision === "day" ? "date" : f.date_precision === "month" ? "month" : "number";
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      <Field label={t("field.event_date")} htmlFor="e-date" hint={t("entry.date_hint")}>
        <Input id="e-date" type={type} data-testid="entry-date-input" max={type === "date" ? todayISO() : undefined} value={f.event_date} onChange={(e) => setF({ ...f, event_date: e.target.value })} />
      </Field>
      <Field label={t("field.date_precision")} htmlFor="e-prec">
        <Select id="e-prec" data-testid="entry-precision-select" value={f.date_precision} onChange={(e) => setF({ ...f, date_precision: e.target.value, event_date: "" })}>
          {["day", "month", "year"].map((p) => <option key={p} value={p}>{t(`precision.${p}`)}</option>)}
        </Select>
      </Field>
    </div>
  );
}

export function EntriesPanel({ type, d, reload }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("ru") ? "ru" : "en";
  const etype = TYPE[type];
  const cats = useRefList("feasibility_categories");
  const draft = useDraft(`entry-${d.dog.id}-${etype}`, BLANK);
  const f = draft.state;
  const setF = draft.setState;
  const online = useOnline();
  const [apiErr, setApiErr] = useState(null);
  const [filterRegion, setFilterRegion] = useState(null);
  const { list, errFor } = useFieldErrors(apiErr);
  const entries = d.entries.filter((e) => e.type === etype);
  const counts = entries.reduce((a, e) => ({ ...a, [e.region]: (a[e.region] || 0) + 1 }), {});
  const shown = filterRegion ? entries.filter((e) => e.region === filterRegion) : entries;
  const dirty = JSON.stringify(f) !== JSON.stringify(BLANK);

  const submit = async (e) => {
    e.preventDefault();
    if (!online) return draft.setPending(true);
    setApiErr(null);
    try {
      const body = { ...f, type: etype, client_id: draft.clientId, event_date: f.event_date ? String(f.event_date) : null };
      await api.post(`/dogs/${d.dog.id}/entries`, body);
      draft.reset(BLANK);
      reload();
    } catch (er) {
      const p = parseError(er);
      if (p.network) draft.setPending(true); else setApiErr(p);
    }
  };
  const pickRegion = (r) => { setF({ ...f, region: r }); setFilterRegion(r); };
  const catLabel = (c) => cats.find((x) => x.code === c)?.label?.[lang] || c;

  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(0,420px)_1fr]">
      <Card>
        <h2 className="mb-1 text-lg font-bold">{t(`entry.add_${etype}`)}</h2>
        {etype === "hypothesis" && <p className="mb-3 text-sm text-muted-foreground">{t("entry.hypothesis_note")}</p>}
        <form onSubmit={submit} className="space-y-4" noValidate data-testid={`entry-form-${etype}`}>
          <DraftBanner pending={draft.pending} restored={draft.restored} dirty={dirty} />
          <ErrorSummary errors={list} />
          {etype === "observation" && (
            <>
              <BodyMap value={f.region} onSelect={pickRegion} counts={counts} />
              <details className="rounded-xl border bg-white p-3"><summary className="cursor-pointer text-sm font-semibold" data-testid="region-text-list-toggle">{t("bodymap.text_list")}</summary><div className="mt-2"><RegionList value={f.region} onSelect={pickRegion} counts={counts} /></div></details>
              {errFor("region") && <p className="text-sm text-destructive" role="alert">{t("field.region")}: {errFor("region")}</p>}
              {f.region && <p data-testid="selected-region" className="text-sm font-semibold">{t("field.region")}: {t(`region.${f.region}`)}</p>}
              <Field label={t("field.side")} htmlFor="e-side"><Select id="e-side" data-testid="entry-side-select" value={f.side} onChange={(e) => setF({ ...f, side: e.target.value })}>{SIDES.map((s) => <option key={s} value={s}>{t(`side.${s}`)}</option>)}</Select></Field>
              <Field label={t("field.sign")} htmlFor="e-sign" error={errFor("sign")} required><Input id="e-sign" data-testid="entry-sign-input" value={f.sign} onChange={(e) => setF({ ...f, sign: e.target.value })} /></Field>
              <Field label={t("field.description")} htmlFor="e-desc"><Textarea id="e-desc" data-testid="entry-description-input" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
              <Field label={t("field.comparison_conditions")} htmlFor="e-cmp"><Input id="e-cmp" data-testid="entry-comparison-input" value={f.comparison_conditions} onChange={(e) => setF({ ...f, comparison_conditions: e.target.value })} /></Field>
            </>
          )}
          {(etype === "restriction" || etype === "hypothesis") && (
            <Field label={t(etype === "restriction" ? "field.restriction_text" : "field.hypothesis_text")} htmlFor="e-text" error={errFor("text")} required>
              <Textarea id="e-text" data-testid="entry-text-input" value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} />
            </Field>
          )}
          {etype === "feasibility" && (
            <>
              <div className="grid gap-3 sm:grid-cols-3">
                <Field label={t("field.procedure")} htmlFor="e-proc" error={errFor("procedure")} required><Input id="e-proc" data-testid="entry-procedure-input" value={f.procedure} onChange={(e) => setF({ ...f, procedure: e.target.value })} /></Field>
                <Field label={t("field.tool")} htmlFor="e-tool"><Input id="e-tool" data-testid="entry-tool-input" value={f.tool} onChange={(e) => setF({ ...f, tool: e.target.value })} /></Field>
                <Field label={t("field.area")} htmlFor="e-area"><Input id="e-area" data-testid="entry-area-input" value={f.area} onChange={(e) => setF({ ...f, area: e.target.value })} /></Field>
              </div>
              <Field label={t("field.observed_response")} htmlFor="e-resp"><Textarea id="e-resp" data-testid="entry-response-input" value={f.observed_response} onChange={(e) => setF({ ...f, observed_response: e.target.value })} /></Field>
              <Field label={t("field.conditions")} htmlFor="e-cond"><Input id="e-cond" data-testid="entry-conditions-input" value={f.conditions} onChange={(e) => setF({ ...f, conditions: e.target.value })} /></Field>
              <Field label={t("field.assistance_required")} htmlFor="e-assist"><Input id="e-assist" data-testid="entry-assistance-input" value={f.assistance_required} onChange={(e) => setF({ ...f, assistance_required: e.target.value })} /></Field>
              <Field label={t("field.next_step")} htmlFor="e-next"><Input id="e-next" data-testid="entry-next-step-input" value={f.next_step} onChange={(e) => setF({ ...f, next_step: e.target.value })} /></Field>
              <Field label={t("field.category")} htmlFor="e-cat" error={errFor("category")} hint={t("entry.category_hint")} required>
                <Select id="e-cat" data-testid="entry-category-select" value={f.category || ""} onChange={(e) => setF({ ...f, category: e.target.value || null })}>
                  <option value="">{t("common.choose")}</option>{cats.map((c) => <option key={c.code} value={c.code}>{c.label[lang]}</option>)}
                </Select>
              </Field>
            </>
          )}
          <Field label={t("field.source")} htmlFor="e-src" error={errFor("source")} required><SourceSelect id="e-src" testid="entry-source-select" value={f.source} onChange={(s) => setF({ ...f, source: s })} /></Field>
          <DateInput f={f} setF={setF} />
          {errFor("event_date") && <p className="text-sm text-destructive" role="alert">{errFor("event_date")}</p>}
          {etype !== "hypothesis" && (
            <div>
              <p className="mb-1 text-sm font-semibold">{t("entry.attach")}</p>
              <FileUploader dogId={d.dog.id} compact meta={{ source: f.source || "groomer_observation", region: f.region || "", capture_date: f.date_precision === "day" ? f.event_date : "" }}
                onAdded={(file) => draft.setState((s) => ({ ...s, file_ids: [...(s.file_ids || []), file.id] }))} />
            </div>
          )}
          <Btn type="submit" testid={`entry-save-btn`}>{t(`entry.save_${etype}`)}</Btn>
        </form>
      </Card>
      <div className="space-y-3">
        {etype === "observation" && filterRegion && (
          <Notice testid="area-history">{t("entry.area_history", { region: t(`region.${filterRegion}`), count: shown.length })} <button className="ml-2 font-semibold underline" onClick={() => setFilterRegion(null)}>{t("entry.show_all")}</button></Notice>
        )}
        {etype === "hypothesis" && <Notice tone="warn" testid="hypothesis-label">{t("entry.hypothesis_banner")}</Notice>}
        {shown.length === 0 && <p className="text-muted-foreground">{t("entry.none")}</p>}
        <ul className="space-y-3" data-testid={`entries-list-${etype}`}>
          {shown.map((e) => <EntryCard key={e.id} e={e} files={d.files.filter((x) => (e.file_ids || []).includes(x.id))} catLabel={catLabel} />)}
        </ul>
      </div>
    </div>
  );
}

function EntryCard({ e, files, catLabel }) {
  const { t } = useTranslation();
  return (
    <li data-testid={`entry-${e.number}`} className="rounded-2xl border bg-white p-4 fade-up">
      <div className="flex flex-wrap items-center gap-2">
        <Tag>{e.number}</Tag>
        {e.type === "hypothesis" && <Tag className="bg-[hsl(42_90%_90%)] text-[hsl(32_80%_24%)]">{t("entry.hypothesis_tag")}</Tag>}
        {e.region && <b>{t(`region.${e.region}`)}{e.side ? ` · ${t(`side.${e.side}`)}` : ""}</b>}
        {e.procedure && <b>{e.procedure}</b>}
        {e.category && <StatusChip status="draft" label={catLabel(e.category)} testid={`feasibility-category-${e.category}`} />}
        <SourceTag source={e.source} />
      </div>
      <p className="mt-2 break-words">{e.sign || e.text || e.observed_response}</p>
      {e.description && <p className="text-sm text-muted-foreground">{e.description}</p>}
      {e.type === "feasibility" && (
        <dl className="mt-2 grid gap-1 text-sm sm:grid-cols-2">
          {["tool", "area", "conditions", "assistance_required", "next_step"].map((k) => e[k] ? <div key={k}><dt className="inline font-semibold">{t(`field.${k}`)}: </dt><dd className="inline">{e[k]}</dd></div> : null)}
        </dl>
      )}
      {files.length > 0 && <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4">{files.map((f) => <FileThumb key={f.id} file={f} />)}</div>}
      <p className="mt-2 text-xs text-muted-foreground">{t("entry.meta", { date: fmtDate(e.event_date, e.date_precision), author: e.author_name })} · {t("entry.entered", { date: fmtDate(e.created_at) })}</p>
    </li>
  );
}
